import React, { useMemo, useState } from 'react';
import { amActions } from '../amStore';
import { COLORS, OTD_TARGET } from '../amConfig';
import { POD_BUCKETS, monthLabel } from '../amRules';
import { groupBy, pct } from '../amMetrics';
import {
  Panel, KpiCard, ShareBar, Chart, clickTo, SortTable, fmtInt, fmtPct, ragColor,
} from '../amUi';

const BUCKET_COLORS = {
  'On-time': COLORS.green, '1-2 days': COLORS.steel[4], '3-5 days': COLORS.steel[3],
  '6-10 days': COLORS.steel[1], 'Above 10 days': COLORS.red, Blank: COLORS.grey,
};

function podStats(items) {
  const received = items.filter((r) => r.pod === 'Received').length;
  const total = items.length;
  return { total, received, pending: total - received, receivedPct: pct(received, total), pendingPct: pct(total - received, total) };
}

const podColumns = (label) => [
  { key: 'name', label, render: (r) => <span className="am-name">{r.label || r.name}</span>, value: (r) => r.name },
  { key: 'total', label: 'Total LRs', align: 'right', bar: true, render: (r) => fmtInt(r.total) },
  { key: 'received', label: 'POD received', align: 'right', render: (r) => fmtInt(r.received) },
  { key: 'pending', label: 'POD pending', align: 'right', render: (r) => <b>{fmtInt(r.pending)}</b> },
  { key: 'receivedPct', label: 'Received %', align: 'right', render: (r) => <b style={{ color: ragColor(r.receivedPct) }}>{fmtPct(r.receivedPct)}</b> },
  { key: 'pendingPct', label: 'Pending %', align: 'right', render: (r) => fmtPct(r.pendingPct) },
];

export default function PodPage({ ctx }) {
  const { s, rows, drillToExplorer } = ctx;
  const podOn = (key, v) => s.filters[key].length === 1 && s.filters[key][0] === v;
  const delivered = useMemo(() => rows.filter((r) => r.delivered), [rows]);
  const k = useMemo(() => podStats(delivered), [delivered]);
  const noPodData = delivered.length > 0 && k.received === 0;
  // Trend drill: month -> transporter -> LRs (explorer).
  const [month, setMonth] = useState(null);

  const tptRows = useMemo(() => [...groupBy(delivered, 'transporter').entries()]
    .map(([name, items]) => ({ name, ...podStats(items) })), [delivered]);

  const buckets = useMemo(() => {
    const g = groupBy(delivered, 'podBucket');
    return POD_BUCKETS.map((b) => ({ name: b, count: (g.get(b) || []).length }));
  }, [delivered]);
  const ageChart = useMemo(() => ({
    chart: { type: 'column' },
    xAxis: { categories: buckets.map((b) => b.name) },
    yAxis: { title: { text: 'Delivered LRs' } },
    legend: { enabled: false },
    plotOptions: { series: clickTo((cat) => amActions.toggle('podBucket', cat)) },
    tooltip: { shared: false, formatter() { return `<b>${this.x}</b><br/>${fmtInt(this.y)} LRs (${fmtPct(pct(this.y, k.total))})`; } },
    series: [{ name: 'LRs', data: buckets.map((b) => ({ y: b.count, color: BUCKET_COLORS[b.name] })),
      dataLabels: { enabled: true, formatter() { return `${fmtInt(this.y)}<br/><span style="font-weight:400">${fmtPct(pct(this.y, k.total))}</span>`; }, style: { fontSize: '10px' } } }],
  }), [buckets, k.total]);

  const months = useMemo(() => [...groupBy(delivered, 'month').entries()].sort((a, b) => a[0].localeCompare(b[0]))
    .map(([m, items]) => ({ name: m, label: monthLabel(m), items, ...podStats(items) })), [delivered]);
  const trendChart = useMemo(() => ({
    chart: { type: 'column' },
    xAxis: { categories: months.map((m) => m.label) },
    yAxis: { max: 100, endOnTick: false, title: { text: '% of delivered LRs' }, labels: { format: '{value}%' },
      plotLines: [{ value: OTD_TARGET, color: COLORS.navy, dashStyle: 'Dash', width: 1.5, zIndex: 4 }] },
    plotOptions: {
      column: { stacking: 'percent' },
      series: clickTo((cat, pt) => {
        const m = months[pt.index].name;
        const clearing = s.filters.month.length === 1 && s.filters.month[0] === m;
        amActions.toggle('month', m);
        setMonth(clearing ? null : m);
      }),
    },
    tooltip: { shared: true, pointFormat: '<span style="color:{series.color}">●</span> {series.name}: <b>{point.y:,.0f}</b> ({point.percentage:.1f}%)<br/>' },
    series: [
      { name: 'Received', color: COLORS.green, data: months.map((m) => m.received) },
      { name: 'Pending', color: COLORS.red, data: months.map((m) => m.pending) },
    ],
  }), [months, s.filters.month]);

  const monthTpt = useMemo(() => {
    const m = months.find((x) => x.name === month);
    if (!m) return [];
    return [...groupBy(m.items, 'transporter').entries()].map(([name, items]) => ({ name, ...podStats(items) }));
  }, [months, month]);

  return (
    <>
      {noPodData && (
        <div className="am-banner">
          No delivered LR in this selection has a POD uploaded yet (is_submitted_pod = 1), so every delivered LR shows as pending.
        </div>
      )}
      <div className="am-kpis five">
        <KpiCard label="Total LR count" value={fmtInt(k.total)} sub="Delivered LRs" onClick={() => amActions.toggle('status', 'Delivered')}
          active={podOn('status', 'Delivered')} hint="Show delivered LRs only" />
        <KpiCard label="POD received" value={fmtInt(k.received)} onClick={() => amActions.toggle('pod', 'Received')} active={podOn('pod', 'Received')} hint="Show LRs with POD received" />
        <KpiCard label="POD pending" value={fmtInt(k.pending)} onClick={() => amActions.toggle('pod', 'Pending')} active={podOn('pod', 'Pending')} hint="Show LRs with POD pending" />
        <KpiCard label="POD received" unit="%" value={fmtPct(k.receivedPct)} accent={ragColor(k.receivedPct)} sub={`Target ${OTD_TARGET}%`}
          onClick={() => amActions.toggle('pod', 'Received')} active={podOn('pod', 'Received')} hint="Show LRs with POD received" />
        <KpiCard label="POD pending" unit="%" value={fmtPct(k.pendingPct)} sub="Oldest backlog in the ageing chart"
          onClick={() => amActions.toggle('pod', 'Pending')} active={podOn('pod', 'Pending')} hint="Show LRs with POD pending" />
      </div>
      <div className="am-grid two">
        <Panel title="Overall POD status" subtitle="Delivered LRs only">
          <ShareBar total={k.total} onSelect={(v) => amActions.toggle('pod', v)}
            items={[{ name: 'Received', count: k.received, color: COLORS.green }, { name: 'Pending', count: k.pending, color: COLORS.red }]} />
          <div className="am-statline">
            <div><b>{fmtInt(k.received)}</b><span>Received · {fmtPct(k.receivedPct)}</span></div>
            <div><b>{fmtInt(k.pending)}</b><span>Pending · {fmtPct(k.pendingPct)}</span></div>
            <div><b>{fmtInt(k.total)}</b><span>Total LRs</span></div>
          </div>
        </Panel>
        <Panel title="POD ageing" subtitle="Days from delivery to POD receipt (pending: to today) · click a bucket to filter">
          <Chart options={ageChart} height={240} />
        </Panel>
      </div>
      <Panel title="Transporter-wise POD compliance" subtitle="Highest pending backlog first · click to filter">
        <SortTable columns={podColumns('Transporter')} rows={tptRows} defaultSort={{ key: 'pending', dir: 'desc' }} maxRows={15}
          onRowClick={(r) => amActions.toggle('transporter', r.name)}
          footer={(
            <tr>
              <td>Total</td><td className="r">{fmtInt(k.total)}</td><td className="r">{fmtInt(k.received)}</td>
              <td className="r">{fmtInt(k.pending)}</td><td className="r">{fmtPct(k.receivedPct)}</td><td className="r">{fmtPct(k.pendingPct)}</td>
            </tr>
          )} />
      </Panel>
      <Panel title="POD trend" subtitle="Received vs pending by dispatch month · click a month to filter the dashboard and list its transporters">
        <Chart options={trendChart} height={260} />
        {month && (
          <div className="am-drill">
            <div className="am-crumbs">
              <button type="button" onClick={() => setMonth(null)}>All months</button><span>›</span>
              <button type="button" className="on">{monthLabel(month)}</button>
              <span className="am-crumb-hint">Click a transporter to open its LRs</span>
            </div>
            <SortTable columns={podColumns('Transporter')} rows={monthTpt} defaultSort={{ key: 'pending', dir: 'desc' }} maxRows={10}
              onRowClick={(r) => drillToExplorer({ month: [month], transporter: [r.name], status: ['Delivered'] })} />
          </div>
        )}
      </Panel>
    </>
  );
}
